import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HardHat, CheckCircle2, Circle, Flag } from "lucide-react";

export default function StaffOperators() {
  const { user } = useAuth();
  const isManagement = user?.role === "management_admin";
  const [operators, setOperators] = useState(null);
  const [ct, setCt] = useState({ url: "", note: "" });
  const [savingCt, setSavingCt] = useState(false);

  useEffect(() => {
    api.get("/staff/operators").then((r) => setOperators(r.data)).catch(() => toast.error("Could not load operators"));
    api.get("/admin/development/settings")
      .then((r) => setCt({ url: r.data.connecteam_url || "", note: r.data.connecteam_note || "" }))
      .catch(() => {});
  }, []);

  const saveConnecteam = async () => {
    setSavingCt(true);
    try {
      await api.put("/admin/development/settings", { connecteam_url: ct.url, connecteam_note: ct.note });
      toast.success("Connecteam settings saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingCt(false); }
  };

  if (!operators) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="operators-loading" />;

  return (
    <div className="space-y-6" data-testid="staff-operators-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Development</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="operators-heading">Operators</h1>
        <p className="text-sm text-slate-500 mt-1">Qualified Development Operators — onboarding, action plans, live experience, competence and funding.</p>
      </div>

      {isManagement && (
        <section className="bg-white border border-steel rounded-md p-5" data-testid="connecteam-config">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm mb-3">Connecteam Configuration</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={ct.url} onChange={(e) => setCt({ ...ct, url: e.target.value })} placeholder="https://app.connecteam.com/..."
              className="bg-white border-steel text-charcoal h-11" data-testid="connecteam-url" />
            <div className="flex gap-2">
              <Button onClick={saveConnecteam} disabled={savingCt} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="connecteam-save-btn">
                {savingCt ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
          <Textarea value={ct.note} onChange={(e) => setCt({ ...ct, note: e.target.value })}
            placeholder="Note shown to operators, e.g. 'Complete the assigned Lomond policies, RAMS, defect-sheet training and timesheet setup in Connecteam.'"
            className="bg-white border-steel text-charcoal mt-3" data-testid="connecteam-note-input" />
        </section>
      )}

      {!operators.length && (
        <p className="text-sm text-slate-500 bg-white border border-steel rounded-md p-6" data-testid="no-operators">
          No operators yet — trainees convert to Qualified Development Operators after external training approval.
        </p>
      )}

      <div className="grid lg:grid-cols-2 gap-4" data-testid="operator-list">
        {operators.map((o) => (
          <Link key={o.id} to={`/staff/operators/${o.id}`} data-testid={`operator-card-${o.id}`}>
            <div className="bg-white border border-steel rounded-md p-5 hover:border-lomond transition-colors h-full">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-bold uppercase text-charcoal flex items-center gap-2">
                    <HardHat className="h-4 w-4 text-lomond" /> {o.name}
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    {(o.operator_grade === "development" ? "Qualified Development Operator" : o.operator_grade) || "Operator"} — Week {o.weeks_employed}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                  o.workplace_ready ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-elevated border-steel text-slate-500"
                }`} data-testid={`ready-${o.id}`}>
                  {o.workplace_ready ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  {o.workplace_ready ? "Workplace Ready" : "Onboarding"}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm">{o.onboarding_done}/10</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Onboarding</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm" data-testid={`hours-${o.id}`}>{o.seat_hours}h</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Seat hours</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm">{o.current_plan ? `AP${o.current_plan.plan_number}` : "—"}</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">{o.current_plan ? o.current_plan.period_label : "No plan"}</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className={`font-mono font-bold text-sm ${o.reminders ? "text-amber-600" : "text-charcoal"}`}>{o.reminders || "—"}</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Reminders</p>
                </div>
              </div>
              {o.ready_for_review && (
                <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1" data-testid={`review-flag-${o.id}`}>
                  <Flag className="h-3 w-3" /> Ready for competence review
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
